import { ArrowDownward, ArrowUpward, Sort } from '@mui/icons-material';
import {
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { type ReactNode, useState } from 'react';
import { getNextSortValue, type SortValue } from './sortDrawerUtils';

export type { SortDirection, SortValue } from './sortDrawerUtils';

export type SortOption = {
  field: string;
  label: ReactNode;
};

export type SortDrawerProps = {
  options: SortOption[];
  value: SortValue[];
  onChange: (value: SortValue[]) => void;
  fullWidth?: boolean;
  title: ReactNode;
};

export function SortDrawer({ options, value, onChange, fullWidth = false, title }: SortDrawerProps) {
  const [open, setOpen] = useState(false);

  function handleSortChange(field: string) {
    onChange(getNextSortValue(value, field));
  }

  return (
    <>
      <IconButton
        data-qa-element-id="sort_button"
        onClick={() => setOpen(true)}
        sx={{
          color: 'primary.main',
          alignItems: 'center',
          width: '64px',
        }}
      >
        <Badge badgeContent={value.length} color={value.length > 0 ? 'primary' : 'default'}>
          <Sort />
        </Badge>
      </IconButton>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          root: {
            sx: {
              zIndex: (theme) => theme.zIndex.modal,
            },
          },
          paper: {
            sx: {
              bottom: 'var(--bottom-nav-bar-height)',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Box data-qa-element-id="sort_order_panel" sx={{ width: fullWidth ? 'auto' : 300, p: 2 }}>
          <Typography data-qa-element-id="sort_order_panel_title" variant="h6" gutterBottom fontWeight={500}>
            {title}
          </Typography>

          <List>
            {options.map((option) => {
              const sortEntry = value.find((item) => item.field === option.field);
              const isAnotherFieldSorted = value.length > 0 && value[0].field !== option.field;

              return (
                <ListItem data-qa-element-id={`sort_by_${option.field}`} key={option.field} disablePadding>
                  <ListItemButton onClick={() => handleSortChange(option.field)} disabled={isAnotherFieldSorted}>
                    <ListItemIcon data-qa-element-id="sort_order_icon">
                      {sortEntry?.direction === 'asc' && <ArrowUpward />}
                      {sortEntry?.direction === 'desc' && <ArrowDownward />}
                    </ListItemIcon>
                    <ListItemText>{option.label}</ListItemText>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </>
  );
}
